// src/components/Header.tsx
import React from 'react';
import { Bell, Search, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const Header: React.FC = () => {
  const { aspirant, user } = useAuth();

  // Get aspirant position safely
  const getAspirantPositionInitial = () => {
    if (!aspirant) return '';
    
    // Type assertion to access position
    const typedAspirant = aspirant as any;
    
    // Check if position exists and is a string
    if (typedAspirant.position && typeof typedAspirant.position === 'string') {
      return typedAspirant.position.charAt(0);
    }
    
    // Fallback to first letter of name
    if (typedAspirant.name && typeof typedAspirant.name === 'string') {
      return typedAspirant.name.charAt(0).toUpperCase();
    }
    
    return 'A';
  };

  // Get display position
  const getDisplayPosition = () => {
    if (!aspirant) return '';
    
    const typedAspirant = aspirant as any;
    
    if (typedAspirant.position) {
      // If position is a string like "MCA" or "MP"
      if (typeof typedAspirant.position === 'string') {
        return typedAspirant.position;
      }
      // If position is an object with a name property
      if (typeof typedAspirant.position === 'object' && typedAspirant.position.name) {
        return typedAspirant.position.name;
      }
    }
    
    return 'Aspirant';
  };

  // Calculate days left if we have dates
  const getDaysLeft = () => {
    if (!aspirant) return 'N/A';
    
    const typedAspirant = aspirant as any;
    
    // Check if subscriptionDaysLeft exists (from your types file)
    if (typedAspirant.subscriptionDaysLeft) {
      return typedAspirant.subscriptionDaysLeft;
    }
    
    // Fallback to calculate from dates
    if (typedAspirant.subscriptionStartDate && typedAspirant.subscriptionEndDate) {
      const start = new Date(typedAspirant.subscriptionStartDate);
      const end = new Date(typedAspirant.subscriptionEndDate);
      const today = new Date();
      
      if (today > end) return 'Expired';
      
      const diffTime = end.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays} days`;
    }
    
    return 'N/A';
  };

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Search */}
          <div className="flex-1 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="search"
                placeholder="Search reports, aspirants, or wards..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-4">
            {/* Language switcher */}
            <button className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100">
              <Globe size={20} />
              <span className="font-medium">SW</span>
            </button>

            {/* Notifications */}
            <button className="relative p-2 rounded-full hover:bg-gray-100">
              <Bell size={20} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* User/Aspirant info */}
            {(aspirant || user) && (
              <div className="hidden md:flex items-center space-x-3 px-4 py-2 bg-blue-50 rounded-lg">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {aspirant ? getAspirantPositionInitial() : user?.fullName?.charAt(0) || 'U'}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {aspirant ? getDisplayPosition() : user?.role || 'User'}
                  </p>
                  {aspirant && (
                    <p className="text-xs text-gray-500">
                      {getDaysLeft()} left
                    </p>
                  )}
                  {user && !aspirant && (
                    <p className="text-xs text-gray-500">
                      {user.fullName}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};